import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { ResearchBrochure } from "@/components/marketing/ResearchBrochure";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const SERVICES = [
  "Device Validation Study",
  "Endpoint Measurement",
  "Physiological Testing Session",
  "Full Programme",
];

const initial = { name: "", organization: "", role: "", email: "", phone: "", service: SERVICES[0], message: "" };

const Section = ({ title, children, testId }) => (
  <Reveal>
    <div className="border-t border-navy/10 pt-10 mt-16" data-testid={testId}>
      <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-6">{title}</h2>
      <div className="space-y-5 text-sm lg:text-base text-navy/60 leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  </Reveal>
);

export default function IndustryAcademic() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        organization: form.organization,
        role: form.role,
        email: form.email,
        phone: form.phone,
        enquiry_type: `Industry & Academic — ${form.service}`,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="industry-academic-page">
      <Nav />

      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
            Industry & Academic
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] leading-tight max-w-4xl" data-testid="ia-title">
            Physiological Measurement for Research
          </h1>
          <p className="font-serif italic text-xl lg:text-2xl text-navy/80 mt-4 max-w-3xl">
            Reference-standard measurement for device and endpoint validation.
          </p>
          <p className="mt-8 text-base text-navy/60 leading-relaxed max-w-2xl">
            A heart rate reading from a smartwatch is a claim until someone tests it. We
            provide the measurement arm that turns it into evidence.
          </p>
        </motion.div>
      </section>

      <ResearchBrochure webHref="/clinical-research" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Section title="Validation against reference standards" testId="ia-validation">
          <p>
            We validate wearable and clinical devices against accepted reference standards
            — heart rate and rhythm against ECG and Holter, blood pressure against
            validated protocols, cardiac output and function against echocardiography.
          </p>
          <p>
            Testing is done under conditions that reflect real use: rest, exercise,
            recovery and free-living activity.
          </p>
        </Section>

        <Section title="How it is delivered" testId="ia-delivery">
          <p>
            Delivered by accredited clinical scientists with an NHS cardiac diagnostics
            background, at your site or a partner facility. Protocol design, data capture,
            analysis and a report written for its purpose — regulatory, publication or
            investor.
          </p>
        </Section>

        <Section title="Who we work with" testId="ia-who">
          <p>
            We work with device companies, CROs and NIHR-funded academic groups.
          </p>
          <p className="font-medium text-navy border-l-2 border-gold pl-5">
            Tell us the claim you need to support.
          </p>
        </Section>
      </div>

      <section id="enquire" className="bg-navy py-24 mt-16 scroll-mt-24" data-testid="ia-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
                Contact
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
                Tell us the claim you need to support.
              </h2>
              <p className="text-sm text-ivory/60 leading-relaxed max-w-md">
                Share the device, endpoint or measurement you need validated, and we will
                respond within one working day.
              </p>
              <p className="mt-6 text-xs text-ivory/40 leading-relaxed max-w-md border-l-2 border-gold/50 pl-4">
                Please do not include any patient-identifiable information in this form.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/50 bg-ivory p-10" data-testid="ia-form-success">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-3">Enquiry received.</h3>
                <p className="text-sm text-navy/60 leading-relaxed">
                  Thank you — the CVI team will review your requirements and respond within
                  one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="ia-enquiry-form">
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="ia-name-input" className={inputCls} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Organisation</label>
                  <input required value={form.organization} onChange={set("organization")} data-testid="ia-organization-input" className={inputCls} placeholder="Device company / CRO / academic group" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Role</label>
                  <input value={form.role} onChange={set("role")} data-testid="ia-role-input" className={inputCls} placeholder="R&D Lead / Study Manager" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="ia-email-input" className={inputCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Telephone</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="ia-phone-input" className={inputCls} placeholder="07…" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Support Required</label>
                  <select value={form.service} onChange={set("service")} data-testid="ia-service-select" className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={`${labelCls} !text-ivory/50`}>Message</label>
                  <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="ia-message-input" className={inputCls} placeholder="The device or endpoint, the claim you need to support, and your timescale…" />
                </div>

                {error && (
                  <p data-testid="ia-form-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="ia-submit-button"
                  className="sm:col-span-2 w-full bg-gold text-navy font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-champagne transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Contact Us"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaFooter />
    </div>
  );
}
